import { useState } from 'react'
import EmptyState from '../shell/EmptyState'
import ActivityForm from './ActivityForm'
import RetireCatalogEntryForm from './RetireCatalogEntryForm'
import { createActivity, updateActivity, type Activity } from '../../pages/admin/catalogStore'

// The one form open at a time: creating an activity (under a parent, or at the
// top level), or acting on one row.
type Panel = { kind: 'create'; parentId: string | null } | { kind: 'edit' | 'retire'; id: string }

const ACTION =
  'flex min-h-11 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2'

/**
 * docs/product/scheduling-model.md's activities: "a two-level catalog: an
 * activity, and optional sub-activities beneath it." Every activity with its
 * sub-activities beneath it, and forms to create one, rename it, and retire or
 * restore it. An activity with open sub-activities cannot be retired, and a
 * sub-activity cannot be restored beneath a retired parent.
 */
export default function ActivitiesSection({
  activities,
  loaded,
  onChanged,
}: {
  activities: Activity[]
  loaded: boolean
  onChanged: () => Promise<void>
}) {
  const [panel, setPanel] = useState<Panel | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const panelActivity = panel && panel.kind !== 'create' ? (activities.find((activity) => activity.id === panel.id) ?? null) : null
  const openParents = activities.filter((activity) => activity.parentId === null && activity.active)

  function openPanel(next: Panel) {
    setNotice(null)
    setFormError(null)
    setPanel(next)
  }

  function closePanel() {
    setPanel(null)
    setFormError(null)
  }

  async function run(action: () => Promise<{ ok: true } | { ok: false; message: string }>, done: string) {
    setSaving(true)
    setFormError(null)
    const result = await action()
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    await onChanged()
    setPanel(null)
    setNotice(done)
  }

  const create = (values: { code: string; name: string; parentId: string | null }) => run(() => createActivity(values), `Created ${values.name}.`)
  const rename = (name: string) => (panelActivity ? run(() => updateActivity(panelActivity.id, name, panelActivity.active), `Renamed to ${name}.`) : Promise.resolve())
  const retire = () => (panelActivity ? run(() => updateActivity(panelActivity.id, panelActivity.name, false), `Retired ${panelActivity.name}.`) : Promise.resolve())
  const restore = (activity: Activity) => {
    setNotice(null)
    return run(() => updateActivity(activity.id, activity.name, true), `Restored ${activity.name}.`)
  }

  return (
    <section id="activities" aria-labelledby="activities-heading" className="flex flex-col gap-4">
      <div>
        <h2 id="activities-heading" className="text-h3 font-extrabold">
          Activities
        </h2>
        <p className="mt-1 text-small text-ink-muted">What a day&apos;s work is categorized as for the manpower and coverage reports, with sub-activities to refine it.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <button
          type="button"
          aria-expanded={panel?.kind === 'create'}
          onClick={() => openPanel({ kind: 'create', parentId: null })}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
        >
          Create activity
        </button>
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {formError && !panel && (
        <p role="alert" className="text-small font-semibold text-error">
          {formError}
        </p>
      )}

      {panel?.kind === 'create' && (
        <ActivityForm key={`create-${panel.parentId}`} parentId={panel.parentId} parents={openParents} saving={saving} error={formError} onCreate={create} onRename={rename} onCancel={closePanel} />
      )}
      {panel?.kind === 'edit' && panelActivity && (
        <ActivityForm key={panelActivity.id} activity={panelActivity} parents={openParents} saving={saving} error={formError} onCreate={create} onRename={rename} onCancel={closePanel} />
      )}
      {panel?.kind === 'retire' && panelActivity && (
        <RetireCatalogEntryForm
          key={panelActivity.id}
          name={panelActivity.name}
          message={`Retiring ${panelActivity.name} takes it out of the activity pickers. Anything that names it keeps reading it.`}
          blocked={panelActivity.openChildren > 0 ? `${panelActivity.name} has ${panelActivity.openChildren} open ${panelActivity.openChildren === 1 ? 'sub-activity' : 'sub-activities'}. Retire ${panelActivity.openChildren === 1 ? 'it' : 'them'} first.` : undefined}
          confirmLabel="Retire activity"
          saving={saving}
          error={formError}
          onConfirm={retire}
          onCancel={closePanel}
        />
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : activities.length === 0 ? (
        <EmptyState>No activities have been created yet.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-surface border border-line">
          <table aria-label="Activities" className="w-full border-collapse">
            <thead>
              <tr>
                {['Activity', 'Code', 'Status'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {activities.map((activity) => (
                <tr key={activity.id}>
                  <th scope="row" className={`border-b border-line py-2 pr-3 text-left text-small ${activity.parentId ? 'pl-8 font-normal' : 'pl-3 font-bold'}`}>
                    {activity.name}
                    {activity.parentId && <span className="sr-only"> (under {activity.parentName})</span>}
                  </th>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">{activity.code}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">
                    <span
                      className={`rounded-full px-2 py-0.5 text-caption font-bold ${
                        activity.active ? 'bg-success/10 text-success' : 'bg-surface-2 text-ink-muted'
                      }`}
                    >
                      {activity.active ? 'Active' : 'Retired'}
                    </span>
                  </td>
                  <td className="border-b border-line py-2 pr-2 pl-3 text-small">
                    {/* Each button has its own fixed slot, so the buttons line up in
                        every row; a sub-activity has no room for one of its own. */}
                    <div className="flex justify-end gap-2 whitespace-nowrap">
                      <div className="w-14">
                        <button type="button" aria-label={`Edit ${activity.name}`} onClick={() => openPanel({ kind: 'edit', id: activity.id })} className={`${ACTION} w-full`}>
                          Edit
                        </button>
                      </div>
                      <div className="w-40">
                        {activity.parentId === null && activity.active && (
                          <button type="button" aria-label={`Add a sub-activity to ${activity.name}`} onClick={() => openPanel({ kind: 'create', parentId: activity.id })} className={`${ACTION} w-full`}>
                            Add sub-activity
                          </button>
                        )}
                      </div>
                      <div className="w-24">
                        {activity.active ? (
                          <button type="button" aria-label={`Retire ${activity.name}`} onClick={() => openPanel({ kind: 'retire', id: activity.id })} className={`${ACTION} w-full`}>
                            Retire
                          </button>
                        ) : (
                          <button type="button" aria-label={`Restore ${activity.name}`} disabled={saving} onClick={() => void restore(activity)} className={`${ACTION} w-full disabled:opacity-40`}>
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
